import { SearchRedirectSchema } from "@garther/interfaces";
import { useI18n } from "@garther/ui/libs";
import { createFileRoute, useParams, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";

import { Layout } from "@/components";
import { QuestionCard, QuizHeader, QuizHelp, QuizNavigation } from "@/components/quiz";
import { trackQuizComplete, trackQuizStart } from "@/libs/analytics";
import { useAuthStore } from "@/stores/auth.store";
import { calculateQuizScore, getQuizRecommendations, useQuizStore } from "@/stores/quiz.store";

export const Route = createFileRoute("/user/$userId/quiz")({
  component: () => QuizPage(),
  validateSearch: SearchRedirectSchema.parse
});

function QuizPage() {
  const { userId } = useParams({ from: "/user/$userId/quiz" });
  const { redirectUrl } = useSearch({ from: "/user/$userId/quiz" });

  const { curUser } = useAuthStore();
  const { t } = useI18n();
  const {
    currentQuiz,
    questions,
    updateAnswer,
    completeQuiz,
    setCurrentQuiz,
    setLoading,
    isLoading,
    fetchQuestions,
    saveQuizData
  } = useQuizStore();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const initialized = useRef(false);
  const isOwner = userId === curUser?.fbUser?.uid;

  // Initialize quiz data and fetch questions from backend - only once
  useEffect(() => {
    if (isOwner && !initialized.current) {
      initialized.current = true;

      // Fetch questions if we don't have them
      if (questions.length === 0) {
        fetchQuestions();
      }

      // Only initialize quiz if we don't have any current quiz data
      if (!currentQuiz) {
        // Track quiz start
        trackQuizStart(userId, "personality_quiz");

        // Initialize new quiz
        setCurrentQuiz({
          userId,
          completed: false,
          answers: {},
          lastUpdated: new Date()
        });
      }
    }
  }, [isOwner, userId, setCurrentQuiz, fetchQuestions, questions.length, currentQuiz]);

  const layout = useMemo(
    () => (children: React.ReactNode) => (
      <Layout>
        <div className="max-w-2xl mx-auto">{children}</div>
      </Layout>
    ),
    []
  );

  if (!isOwner) {
    return layout(
      <div className="text-center py-8">
        <h2 className="text-textcolor-primary text-xl font-bold mb-4">{t("quiz.page.accessDenied")}</h2>
        <p className="text-textcolor-secondary">{t("quiz.page.accessDeniedDescription")}</p>
      </div>
    );
  }

  if (isLoading && questions.length === 0) {
    return layout(
      <div className="text-center py-8">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-textcolor-secondary">{t("quiz.page.loadingQuiz")}</p>
      </div>
    );
  }

  if (questions.length === 0) {
    return layout(
      <div className="text-center py-8">
        <h2 className="text-textcolor-primary text-xl font-bold mb-4">
          {t("quiz.page.noQuestionsAvailable")}
        </h2>
        <p className="text-textcolor-secondary">{t("quiz.page.noQuestionsDescription")}</p>
      </div>
    );
  }

  // Ensure currentQuestion is within bounds
  if (currentQuestion >= questions.length) {
    setCurrentQuestion(0);
    return layout(
      <div className="text-center py-8">
        <p className="text-textcolor-secondary">{t("quiz.page.loadingQuestion")}</p>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];

  // Safety check for currentQ
  if (!currentQ) {
    return layout(
      <div className="text-center py-8">
        <p className="text-textcolor-secondary">{t("quiz.page.questionNotFound")}</p>
      </div>
    );
  }

  const currentAnswer = currentQuiz?.answers[currentQ.id];

  const handleAnswer = (answer: any) => {
    updateAnswer(currentQ.id, answer);
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion((prev) => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setLoading(true);

    try {
      // Calculate score
      const score = calculateQuizScore(currentQuiz?.answers || {});

      // Track quiz completion
      trackQuizComplete(userId, "personality_quiz", score);

      // Complete the quiz
      completeQuiz(score);

      // Save quiz data to backend
      await saveQuizData();

      // Get personalized recommendations
      const recommendations = getQuizRecommendations(currentQuiz?.answers || {});

      // Store recommendations in localStorage for the profile page
      localStorage.setItem("quizRecommendations", JSON.stringify(recommendations));

      // Redirect to profile page
      window.location.href = redirectUrl || `/user/${userId}/profile`;
    } catch (error) {
      console.error("Error submitting quiz:", error);
      alert(t("quiz.error.submissionFailed"));
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  const canProceed = () => {
    if (currentAnswer === null || currentAnswer === undefined) return false;

    // For single choice questions, answer should be a number (index)
    if (currentQ.type === "single") {
      return typeof currentAnswer === "number" && currentAnswer >= 0;
    }

    // For multiple choice questions, answer should be an array of numbers (indexes) with at least one element
    if (currentQ.type === "multiple") {
      return (
        Array.isArray(currentAnswer) &&
        currentAnswer.length > 0 &&
        currentAnswer.every((item) => typeof item === "number" && item >= 0)
      );
    }

    // For scale/degree questions, answer should be a number
    if (currentQ.type === "scale" || currentQ.type === "degree") {
      return typeof currentAnswer === "number" && currentAnswer >= 0;
    }

    return true;
  };

  return (
    <Layout requireAuth>
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* Header with Progress */}
        <QuizHeader currentQuestion={currentQuestion} totalQuestions={questions.length} />

        {/* Question Card */}
        <QuestionCard question={currentQ} currentAnswer={currentAnswer} onAnswerChange={handleAnswer} />

        {/* Navigation */}
        <QuizNavigation
          currentQuestion={currentQuestion}
          totalQuestions={questions.length}
          canProceed={canProceed}
          isSubmitting={isSubmitting}
          onPrevious={handlePrevious}
          onNext={handleNext}
          onSubmit={handleSubmit}
        />

        {/* Help Section */}
        <QuizHelp />
      </div>
    </Layout>
  );
}
